import { useMemo } from "react";

import { type ResponseStatus, type IModelValidationResponse, type IReturn } from "src/api/Tmd.dtos";
import useServiceStackClient from "src/hooks/api/useServiceStackClient";
import asyncDebounce from "src/utilities/asyncDebounce";

/**
 * Defines the response payload and status expected by this shared request adapter.
 */
export type TMDFormValidationResponse = IModelValidationResponse & {
    responseStatus: ResponseStatus;
};

/**
 * Defines the typed request payload constructed by this shared adapter.
 */
export type TMDFormValidationRequest<
    TResponse extends TMDFormValidationResponse,
    TModel,
    TMetadataContext
> = IReturn<TResponse> & {
    model: TModel;
    context: TMetadataContext;
};

/**
 * Returns an optionally debounced server-validation callback.
 *
 * Postconditions: Each dispatched request posts model and context and fulfills with the raw
 * response. Client errors propagate through the callback.
 *
 * Assumptions: The constructor stays stable; it is absent from the memo dependencies, which
 * remain client and debounce delay.
 */
const useBaseFormValidation = <
    TModel,
    TMetadataContext,
    TResponse extends TMDFormValidationResponse,
    TRequest extends TMDFormValidationRequest<TResponse, TModel, TMetadataContext>
>(
    constructor: new (init?: Partial<TRequest>) => TRequest,
    debounceMs?: number | null | undefined
): ((model: TModel, context: TMetadataContext) => Promise<TResponse>) => {
    const client = useServiceStackClient();

    return useMemo(
        () => {
            /** Posts the supplied model/context and propagates request failures. */
            const validate = async (model: TModel, context: TMetadataContext) => {
                const response = await client.post(
                    new constructor({
                        context,
                        model
                    } as Partial<TRequest>)
                );

                return response;
            };

            if (!debounceMs) {
                return validate;
            }

            return asyncDebounce(validate, debounceMs);
        },
        [client, debounceMs]
    );
};

export default useBaseFormValidation;
